import { Icon, IconNames } from '@triumph/design-system';
import { ReactNode, useEffect, useState } from 'react';
import {
    Body,
    BodyWrapper,
    EndSentinel,
    Footer,
    HeaderTitle,
    ScrollHintChevron,
    ScrollHintFade,
    StyledSidebar,
} from './SideDrawer.styles';

export interface SideDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    position?: 'left' | 'right';
    /** When false, no mask renders and the page underneath stays interactive. */
    modal?: boolean;
    widthPx?: number;
    /**
     * When true, the page content is squeezed away from the drawer (via padding
     * on <body>) so the drawer doesn't cover the underlying UI. Recommended for
     * non-modal drawers where the user should keep interacting with the page.
     */
    pushPageContent?: boolean;
    children: ReactNode;
    footer?: ReactNode;
    /** Accessible label for the X close button. */
    closeLabel?: string;
    /** Test id for the scrollable body element. */
    bodyTestId?: string;
    /** Test id for the scroll-hint chevron (defaults to side-drawer-scroll-hint). */
    scrollHintTestId?: string;
    /** Accessible label for the scroll-hint chevron. */
    scrollHintLabel?: string;
}

export function SideDrawer({
    isOpen,
    onClose,
    title,
    position = 'right',
    modal = false,
    widthPx = 420,
    pushPageContent = false,
    children,
    footer,
    closeLabel,
    bodyTestId,
    scrollHintTestId = 'side-drawer-scroll-hint',
    scrollHintLabel = 'Scroll for more content',
}: SideDrawerProps) {
    const [bodyEl, setBodyEl] = useState<HTMLDivElement | null>(null);
    const [endSentinelEl, setEndSentinelEl] = useState<HTMLDivElement | null>(null);
    const [hasOverflowBelow, setHasOverflowBelow] = useState(false);

    useEffect(() => {
        if (!bodyEl || !endSentinelEl) return;
        const observer = new IntersectionObserver(
            ([entry]) => setHasOverflowBelow(!entry.isIntersecting),
            { root: bodyEl, threshold: 0 },
        );
        observer.observe(endSentinelEl);
        return () => observer.disconnect();
    }, [bodyEl, endSentinelEl]);

    // When this drawer wants to squeeze the page, set the matching --side-drawer-push-*
    // CSS variable on <html> while open. The variable feeds the body padding rule
    // in SideDrawerGlobalStyles. Each drawer manages its own side independently
    // so left- and right-side drawers can coexist without clobbering each other.
    useEffect(() => {
        if (!pushPageContent) return;
        if (typeof document === 'undefined') return;
        const cssVar = position === 'left' ? '--side-drawer-push-left' : '--side-drawer-push-right';
        const root = document.documentElement;
        if (isOpen) {
            root.style.setProperty(cssVar, `${widthPx}px`);
        } else {
            root.style.removeProperty(cssVar);
        }
        return () => {
            root.style.removeProperty(cssVar);
        };
    }, [isOpen, pushPageContent, position, widthPx]);

    const scrollBodyDown = () => {
        if (!bodyEl) return;
        bodyEl.scrollBy({ top: bodyEl.clientHeight * 0.8, behavior: 'smooth' });
    };

    return (
        <StyledSidebar
            visible={isOpen}
            onHide={onClose}
            position={position}
            modal={modal}
            dismissable={modal}
            showCloseIcon
            appendTo={typeof document === 'undefined' ? undefined : document.body}
            header={<HeaderTitle>{title}</HeaderTitle>}
            style={{
                width: `min(${widthPx}px, 100vw)`,
                position: 'fixed',
                top: '64px',
                bottom: '0',
                [position]: '0',
                height: 'auto',
                maxHeight: 'none',
                margin: 0,
            }}
            maskStyle={{ top: '64px', bottom: '0', height: 'auto' }}
            ariaCloseLabel={closeLabel ?? `Close ${title}`}
        >
            <BodyWrapper>
                <Body ref={setBodyEl} data-testid={bodyTestId}>
                    {children}
                    <EndSentinel ref={setEndSentinelEl} aria-hidden="true" />
                </Body>
                <ScrollHintFade visible={hasOverflowBelow} aria-hidden="true" />
                <ScrollHintChevron
                    type="button"
                    visible={hasOverflowBelow}
                    tabIndex={hasOverflowBelow ? 0 : -1}
                    aria-hidden={!hasOverflowBelow}
                    onClick={scrollBodyDown}
                    aria-label={scrollHintLabel}
                    data-testid={scrollHintTestId}
                >
                    <Icon
                        name={IconNames.Arrow_CaretDown}
                        size={18}
                        id={`${scrollHintTestId}-icon`}
                    />
                </ScrollHintChevron>
            </BodyWrapper>
            {footer ? <Footer>{footer}</Footer> : null}
        </StyledSidebar>
    );
}
